import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useDI } from "@/infrastructure/di/useDI";
import { useSeatsViewModel } from "./SeatsViewModel";
import type {
  AccountUserRole,
  PendingAccountInvitation,
  PersonSeatAssignment,
} from "@/domain/model/AccountSeatOverview";
import {
  PageContent,
  SectionCard,
  PageHeader,
  SummaryStatCard,
} from "@/adapters/inbound/layout";
import { formatDate, humanizeRole } from "../UiLabels";
import { Title } from "react-admin";
import { InviteUserDialog } from "../components/InviteUserDialog";

const ROLE_OPTIONS: AccountUserRole[] = ["WORKER", "ADMIN"];
const CHANGE_ROLE_OPTIONS: AccountUserRole[] = ["ADMIN", "WORKER"];

function humanizeState(state: string): string {
  switch (state) {
    case "ACTIVE":
      return "Aktiv";
    case "INVITED":
      return "Eingeladen";
    case "SUSPENDED":
      return "Gesperrt";
    case "UNASSIGNED":
      return "Kein Zugang";
    default:
      return state;
  }
}

function stateColor(
  state: string,
): "default" | "success" | "warning" | "error" {
  switch (state) {
    case "ACTIVE":
      return "success";
    case "INVITED":
      return "warning";
    case "SUSPENDED":
      return "error";
    case "UNASSIGNED":
    default:
      return "default";
  }
}

function isExpired(value: Date): boolean {
  return value.getTime() <= Date.now();
}

function invitationStatusLabel(invitation: PendingAccountInvitation): string {
  return isExpired(invitation.expiresAt) ? "Abgelaufen" : "Ausstehend";
}

function invitationStatusColor(
  invitation: PendingAccountInvitation,
): "default" | "warning" {
  return isExpired(invitation.expiresAt) ? "default" : "warning";
}

function resendButtonLabel(props: {
  isBusy: boolean;
  cooldownUntil: number | null;
  nowMs: number;
}): string {
  if (props.isBusy) return "Wird gesendet …";

  const cooldownUntil = props.cooldownUntil ?? 0;
  const remainingMs = cooldownUntil - props.nowMs;

  if (remainingMs > 0) {
    return `Erneut senden in ${Math.ceil(remainingMs / 1000)}s`;
  }

  return "Erneut senden";
}

function GrantAccessDialog(props: {
  open: boolean;
  person: PersonSeatAssignment | null;
  email: string;
  role: AccountUserRole;
  isBusy: boolean;
  canSubmit: boolean;
  onClose: () => void;
  onEmailChange: (value: string) => void;
  onRoleChange: (value: AccountUserRole) => void;
  onSubmit: () => void;
}) {
  return (
    <Dialog
      open={props.open}
      onClose={props.isBusy ? undefined : props.onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Zugang für Person erstellen</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Person"
            value={props.person?.displayName ?? ""}
            fullWidth
            disabled
          />

          <TextField
            label="E-Mail"
            type="email"
            value={props.email}
            onChange={(event) => props.onEmailChange(event.target.value)}
            fullWidth
            required
            autoFocus
            helperText={
              props.person?.email
                ? "Vorbelegt aus der Person. Sie können die Adresse ändern."
                : "Bitte E-Mail-Adresse für den Zugang eingeben."
            }
          />

          <TextField
            label="Rolle"
            select
            value={props.role}
            onChange={(event) =>
              props.onRoleChange(event.target.value as AccountUserRole)
            }
            fullWidth
          >
            {ROLE_OPTIONS.map((role) => (
              <MenuItem key={role} value={role}>
                {humanizeRole(role)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={props.onClose} disabled={props.isBusy}>
          Abbrechen
        </Button>

        <Button
          variant="contained"
          onClick={props.onSubmit}
          disabled={!props.canSubmit}
        >
          {props.isBusy ? "Wird erstellt …" : "Zugang erstellen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ChangeRoleDialog(props: {
  open: boolean;
  displayName: string;
  currentRole: AccountUserRole | null;
  desiredRole: AccountUserRole;
  isBusy: boolean;
  canSubmit: boolean;
  onClose: () => void;
  onRoleChange: (value: AccountUserRole) => void;
  onSubmit: () => void;
}) {
  return (
    <Dialog
      open={props.open}
      onClose={props.isBusy ? undefined : props.onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Rolle ändern</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Nutzer"
            value={props.displayName}
            fullWidth
            disabled
          />

          <TextField
            label="Aktuelle Rolle"
            value={
              props.currentRole ? humanizeRole(props.currentRole) : "Unbekannt"
            }
            fullWidth
            disabled
          />

          <TextField
            label="Neue Rolle"
            select
            value={props.desiredRole}
            onChange={(event) =>
              props.onRoleChange(event.target.value as AccountUserRole)
            }
            fullWidth
            autoFocus
            helperText="Eigentümerrechte können hier nicht vergeben werden."
          >
            {CHANGE_ROLE_OPTIONS.map((role) => (
              <MenuItem key={role} value={role}>
                {humanizeRole(role)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={props.onClose} disabled={props.isBusy}>
          Abbrechen
        </Button>

        <Button
          variant="contained"
          onClick={props.onSubmit}
          disabled={!props.canSubmit}
        >
          {props.isBusy ? "Wird geändert …" : "Rolle ändern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SeatsPage() {
  const {
    getAccountSeats,
    inviteAccountUser,
    grantPersonAccess,
    removePersonSeat,
    restorePersonSeat,
    resendAccountInvitation,
    revokeInvitation,
    changeAccountUserRole
  } = useDI();

  const { state, derived, intents } = useSeatsViewModel({
    getAccountSeats,
    inviteAccountUser,
    grantPersonAccess,
    removePersonSeat,
    restorePersonSeat,
    resendAccountInvitation,
    revokeInvitation,
    changeAccountUserRole
  });

  const selectedPerson = useMemo(() => {
    const personId = state.grantAccessDialog.personId;
    if (!personId || !state.data) return null;
    return (
      state.data.persons.find((person) => person.personId === personId) ?? null
    );
  }, [state.data, state.grantAccessDialog.personId]);


  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const hasActiveCooldown = Object.values(derived.resendCooldowns).some(
      (until) => until > Date.now(),
    );

    if (!hasActiveCooldown) {
      return;
    }

    setNowMs(Date.now());

    const id = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1_000);

    return () => window.clearInterval(id);
  }, [derived.resendCooldowns]);

  if (state.isInitialLoading) {
    return (
      <PageContent width="content">
        <Typography color="text.secondary">
          Sitzverwaltung wird geladen …
        </Typography>
      </PageContent>
    );
  }

  if (state.loadError || !derived.seatSummary) {
    return (
      <PageContent width="content">
        <Stack spacing={3}>
          <PageHeader title="Sitze & Nutzer" />

          <Alert severity="error">
            {state.loadError ?? "Sitzverwaltung konnte nicht geladen werden."}
          </Alert>

          <Box>
            <Button variant="contained" onClick={() => void intents.reload()}>
              Erneut laden
            </Button>
          </Box>
        </Stack>
      </PageContent>
    );
  }

  return (
    <>
      <Title title="Sitze & Nutzer" />
      <PageContent width="content">
        <Stack spacing={3}>
          <PageHeader
            title="Sitze & Nutzer"
            subtitle="Verwalten Sie Zugänge, Einladungen und die Belegung Ihrer Sitze."
          />

          {state.successMessage ? (
            <Alert severity="success" onClose={intents.clearMessages}>
              {state.successMessage}
            </Alert>
          ) : null}

          {state.actionError ? (
            <Alert severity="error" onClose={intents.clearMessages}>
              {state.actionError}
            </Alert>
          ) : null}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <SummaryStatCard
                label="Gebuchte Sitze"
                value={derived.seatSummary.purchasedSeats}
                sx={{ flex: 1 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <SummaryStatCard
                label="Aktive Sitze"
                value={derived.seatSummary.activeSeats}
                sx={{ flex: 1 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <SummaryStatCard
                label="Freie Sitze"
                value={derived.seatSummary.freeSeats}
                sx={{ flex: 1 }}
              />
            </Grid>
          </Grid>

          {derived.hasNoFreeSeats ? (
            <Alert
              severity="warning"
              action={
                <Button
                  component={RouterLink}
                  to="/dashboard/settings/account/billing"
                  color="inherit"
                  size="small"
                >
                  Sitze erhöhen
                </Button>
              }
            >
              Es sind keine freien Sitze verfügbar.
            </Alert>
          ) : null}

          {derived.pendingInvitations.length > 0 ? (
            <SectionCard
              title="Offene Einladungen"
              subtitle="Einladungen, die noch nicht angenommen wurden."
            >
              <Box sx={{ mt: 3, overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>E-Mail</TableCell>
                      <TableCell>Rolle</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Erstellt</TableCell>
                      <TableCell>Gültig bis</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {derived.pendingInvitations.map((invitation) => {
                      const expired = isExpired(invitation.expiresAt);

                      const isResendBusy =
                        state.activeInvitationId === invitation.invitationId &&
                        state.activeAction === "resendInvitation";

                      const isRevokeBusy =
                        state.activeInvitationId === invitation.invitationId &&
                        state.activeAction === "revokeInvitation";

                      const cooldownUntil =
                        derived.resendCooldowns[invitation.invitationId] ?? null;

                      const isCoolingDown =
                        cooldownUntil !== null && cooldownUntil > Date.now();

                      return (
                        <TableRow key={invitation.invitationId} hover>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="body2" fontWeight={600}>
                                {invitation.email}
                              </Typography>

                              {invitation.personId ? (
                                <Typography variant="caption" color="text.secondary">
                                  Mit Person verknüpft
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Direkte Einladung
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>

                          <TableCell>{humanizeRole(invitation.role)}</TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={invitationStatusLabel(invitation)}
                              color={invitationStatusColor(invitation)}
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell>{formatDate(invitation.createdAt)}</TableCell>

                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="body2">
                                {formatDate(invitation.expiresAt)}
                              </Typography>

                              {expired ? (
                                <Typography variant="caption" color="text.secondary">
                                  Einladung ist nicht mehr gültig
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>

                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                            >
                              <Button
                                size="small"
                                onClick={() =>
                                  void intents.resendInvitation({
                                    invitationId: invitation.invitationId
                                  })
                                }
                                disabled={derived.isBusy || expired || isCoolingDown}
                              >
                                {resendButtonLabel({
                                  isBusy: isResendBusy,
                                  cooldownUntil,
                                  nowMs,
                                })}
                              </Button>

                              <Button
                                size="small"
                                color="error"
                                onClick={() =>
                                  void intents.revokeInvitation({
                                    invitationId: invitation.invitationId,
                                  })
                                }
                                disabled={derived.isBusy}
                              >
                                {isRevokeBusy ? "Wird zurückgezogen …" : "Zurückziehen"}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </SectionCard>
          ) : null}

          <SectionCard
            title="Nutzerzugänge"
            subtitle="Einladungen, bestehende Zugänge und wiederherstellbare Sitze."
            actions={
              <Button
                variant="contained"
                onClick={intents.openInviteDialog}
                disabled={derived.isBusy}
              >
                Nutzer einladen
              </Button>
            }
          >
            <Box sx={{ mt: 3, overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>E-Mail</TableCell>
                    <TableCell>Rolle</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Einladung</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {derived.persons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="text.secondary">
                          Noch keine Personen vorhanden.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {derived.persons.map((person) => {
                    const invitationId = person.invitationId;
                    const rowBusy =
                      state.activePersonId === person.personId &&
                      (state.activeAction === "removeSeat" ||
                        state.activeAction === "restoreSeat" ||
                        state.activeAction === "grantAccess" ||
                        state.activeAction === "revokeInvitation" ||
                        state.activeAction === "changeRole");

                    const canGrantAccess =
                      person.state === "UNASSIGNED" || person.state === "SUSPENDED";

                    const canRevokeInvitation =
                      person.state === "INVITED" && invitationId !== null;
                    const canManageInvitation =
                      person.state === "INVITED" && invitationId !== null;

                    const canRemove = person.state === "ACTIVE";
                    const canRestore = person.state === "SUSPENDED";

                    const isRevokeInvitationBusy =
                      rowBusy && state.activeAction === "revokeInvitation";

                    const cooldownUntil = invitationId
                      ? derived.resendCooldowns[invitationId] ?? null
                      : null;

                    const isCoolingDown =
                      cooldownUntil !== null && cooldownUntil > nowMs;

                    const isResendBusy =
                      rowBusy && state.activeAction === "resendInvitation";

                    const canChangeRole =
                      person.state === "ACTIVE" &&
                      person.userId !== null &&
                      person.role !== null &&
                      person.role !== "OWNER";

                    const isChangeRoleBusy =
                      rowBusy && state.activeAction === "changeRole";

                    return (
                      <TableRow key={person.personId} hover>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                              {person.displayName}
                            </Typography>
                            {person.preferredName ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {person.preferredName}
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>

                        <TableCell>{person.email ?? "—"}</TableCell>
                        <TableCell>{humanizeRole(person.role)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={humanizeState(person.state)}
                            color={stateColor(person.state)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {person.invitationExpiresAt
                            ? `Bis ${formatDate(person.invitationExpiresAt)}`
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            flexWrap="wrap"
                          >
                            {canChangeRole ? (
                              <Button
                                size="small"
                                onClick={() => intents.openChangeRoleDialog(person)}
                                disabled={derived.isBusy}
                              >
                                {isChangeRoleBusy ? "Wird geändert …" : "Rolle ändern"}
                              </Button>
                            ) : null}
                            {canGrantAccess ? (
                              <Button
                                size="small"
                                onClick={() => intents.openGrantAccessDialog(person)}
                                disabled={derived.isBusy}
                              >
                                Zugang erstellen
                              </Button>
                            ) : null}

                            {canRemove ? (
                              <Button
                                size="small"
                                color="warning"
                                onClick={() =>
                                  void intents.removeSeat(person.personId)
                                }
                                disabled={derived.isBusy}
                              >
                                {rowBusy && state.activeAction === "removeSeat"
                                  ? "Entfernt …"
                                  : "Entfernen"}
                              </Button>
                            ) : null}

                            {canRestore ? (
                              <Button
                                size="small"
                                onClick={() =>
                                  void intents.restoreSeat(person.personId)
                                }
                                disabled={derived.isBusy}
                              >
                                {rowBusy && state.activeAction === "restoreSeat"
                                  ? "Wird aktiviert …"
                                  : "Wieder aktivieren"}
                              </Button>
                            ) : null}
                            {canManageInvitation ? (
                              <Button
                                size="small"
                                onClick={() =>
                                  void intents.resendInvitation({
                                    invitationId: invitationId,
                                    personId: person.personId,
                                  })
                                }
                                disabled={derived.isBusy || isCoolingDown}
                              >
                                {resendButtonLabel({
                                  isBusy: isResendBusy,
                                  cooldownUntil,
                                  nowMs,
                                })}
                              </Button>
                            ) : null}
                            {canRevokeInvitation ? (
                              <Button
                                size="small"
                                color="error"
                                onClick={() =>
                                  void intents.revokeInvitation({
                                    invitationId: person.invitationId!,
                                    personId: person.personId,
                                  })
                                }
                                disabled={derived.isBusy}
                              >
                                {isRevokeInvitationBusy
                                  ? "Wird zurückgezogen …"
                                  : "Zurückziehen"}
                              </Button>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Stack>

        <InviteUserDialog
          open={state.inviteDialog.open}
          email={state.inviteDialog.email}
          role={state.inviteDialog.role}
          isBusy={derived.isInviteBusy}
          onClose={intents.closeInviteDialog}
          onEmailChange={intents.setInviteEmail}
          onRoleChange={intents.setInviteRole}
          onSubmit={() => void intents.submitInvite()}
        />

        <GrantAccessDialog
          open={state.grantAccessDialog.open}
          person={selectedPerson}
          email={state.grantAccessDialog.email}
          role={state.grantAccessDialog.role}
          isBusy={derived.isGrantAccessBusy}
          canSubmit={derived.canSubmitGrantAccess}
          onClose={intents.closeGrantAccessDialog}
          onEmailChange={intents.setGrantAccessEmail}
          onRoleChange={intents.setGrantAccessRole}
          onSubmit={() => void intents.submitGrantAccess()}
        />

        <ChangeRoleDialog
          open={state.changeRoleDialog.open}
          displayName={state.changeRoleDialog.displayName}
          currentRole={state.changeRoleDialog.currentRole}
          desiredRole={state.changeRoleDialog.desiredRole}
          isBusy={derived.isChangeRoleBusy}
          canSubmit={derived.canSubmitChangeRole}
          onClose={intents.closeChangeRoleDialog}
          onRoleChange={intents.setChangeRole}
          onSubmit={() => void intents.submitChangeRole()}
        />
      </PageContent>
    </>
  );
}
